import * as types from "./../action-types";
import axios from "axios";
import { get, isEmpty, throttle } from "lodash";

const cleanUpStore = (commit) => {
  commit("db/account/resetState", null, { root: true });
  commit("db/devices/resetState", null, { root: true });
  commit("db/requests/resetState", null, { root: true });
  commit("db/account/location/resetState", null, { root: true });
};

const cleanUpAccountVerificationStore = (commit) => {
  commit("db/account/verification/resetState", null, { root: true });
};

const fetchAuthorizationChannelsV3 = throttle((commit, customerId) => {
  axios
    .get(`/v3/customers/${customerId}/account_verification/channels`)
    .then((response) => {
      commit(types.SET_VERIFICATION_CHANNELS, response.data);
    })
    .catch((error) => {
      console.log(
        `Error getting allowed verification methods: ${JSON.stringify(error)}`
      );
    });
}, 1000);

const getActiveAuthorizationChannels = ({
  state,
  commit,
  getters,
  rootGetters,
}) => {
  let currentCustomerId = String(rootGetters["db/account/customerId"]);
  fetchAuthorizationChannelsV3(commit, currentCustomerId);
};

const setIsFirstAuthorizationAttempt = (
  { state, commit, getters, rootGetters },
  value
) => {
  commit(types.IS_FIRST_VERIFICATION_ATTEMPT, value);
};

async function globalSearch(
  { state, commit, getters, rootGetters },
  {
    model = "error",
    criteria,
    includes,
    keep_previous_data = false,
    do_nothing_if_same_id = false,
  }
) {
  let currentCustomerId = String(rootGetters["db/account/customerId"]);
  return new Promise((resolve, reject) => {
    let params = { includes: ["basic_profile", "amazon_profile", "locations"] };
    const field = String(criteria).match(/@/) ? "email" : "customer_id";
    if (includes) params.includes = params.includes.concat(includes);
    params.includes = params.includes.filter(function (v, i) {
      return params.includes.indexOf(v) === i;
    }); // removes duplicated

    const isV3 = rootGetters["auth/isNewLeftNav"] === true;

    let axiosRequest;
    if (isV3) {
      axiosRequest = getSearchCustomerEndpoint(criteria, params.includes);
    } else {
      params[field] = criteria;
      axiosRequest = axios.post("v2/customers/load_by_email_or_id", params);
    }

    axiosRequest
      .then((response) => {
        // only clean up the store HERE, that is, IF the search is successful otherwise let the current customer as it is.
        let foundCustomerId = String(get(response, "data.customer.id", null));

        const isSameCustomer = currentCustomerId === foundCustomerId;
        if (!isSameCustomer) {
          cleanUpAccountVerificationStore(commit);
        }

        if (do_nothing_if_same_id && isSameCustomer) {
          return resolve({ do_nothing: true });
        } else {
          if (keep_previous_data === false) cleanUpStore(commit);
          commit(types.GLOBAL_SEARCH.SUCCESS, {
            model,
            result: response.data,
            update: keep_previous_data === false,
          });
          resolve({
            found: Boolean(get(response, "data.customer", null)),
            email: get(response, "data.customer.email", "search_error"),
            id: get(response, "data.customer.id", null),
          });
        }
      })
      .catch((error) => {
        commit(types.GLOBAL_SEARCH.ERROR, error);
        reject(error);
      });
  });
}

async function getSearchCustomerEndpoint(valueToSearch, params) {
  const fieldType = String(valueToSearch).match(/@/) ? "email" : "customer_id";
  const includes = params.map((p) => `includes[]=${p}`);

  let queryParams = "";
  if (!isEmpty(includes)) {
    queryParams = includes.join("&");
    queryParams = `?${queryParams}`;
  }

  if (fieldType === "email") {
    const config = {
      headers: {
        email: encodeURIComponent(valueToSearch),
      },
    };

    return await axios.get(`v3/customers${queryParams}`, config);
  } else {
    return await axios.get(`v3/customers/${valueToSearch}${queryParams}`);
  }
}

async function updateCustomer({ commit }, { id, payload }) {
  try {
    await axios.put(`/v2/customers/${id}`, payload);
    commit(types.UPDATE_CUSTOMER_PROFILE, payload);
  } catch (error) {
    throw error.response;
  }
}

async function updateCustomerEmail({ commit }, { id, email }) {
  try {
    await axios.post(`/v2/customers/${id}/update_email.json`, {
      new_email: email,
    });
    commit(types.UPDATE_CUSTOMER_PROFILE, {
      data: { email: email, profile: { email: email } },
    });
    return true;
  } catch (error) {
    throw error;
  }
}

async function fetchAppActivityProfile({ state, commit, getters }) {
  const id = getters.customerId;
  try {
    const response = await axios.get(
      `/v2/customers/${id}/application_activity/profile`
    );
    const props = get(response.data.pop(), "properties", {});
    commit(types.FETCH_ACCOUNT_APP_ACTIVITY_PROFILE.SUCCESS, props);
  } catch (error) {
    commit(types.FETCH_ACCOUNT_APP_ACTIVITY_PROFILE.ERROR, error);
    throw error.response;
  }
}

async function resetPassword({ state, commit, getters }) {
  const id = getters.customer.id;
  const email = getters.customer.email;
  try {
    const response = await axios.post(`/v2/customers/${id}/reset_password`, {
      id: id,
      email: email,
    });
    return response;
  } catch (error) {
    throw error.response;
  }
}

async function fetchAppActivityEvents({ getters, commit }, { page }) {
  const id = getters.customerId;
  try {
    const response = await axios.get(
      `/v2/customers/${id}/application_activity/events`,
      { params: { page } }
    );
    commit(types.FETCH_ACCOUNT_APP_ACTIVITY_EVENTS.SUCCESS, {
      activityEvents: response.data,
      page,
    });
  } catch (error) {
    commit(types.FETCH_ACCOUNT_APP_ACTIVITY_EVENTS.ERROR, error.response);
    throw error.response;
  }
}

async function fetchClientDevices(
  { state, commit },
  { page, pageSize, customerId }
) {
  page = page || 1;
  pageSize = pageSize || 10;
  try {
    const response = await axios.get(
      `v2/customers/${customerId}/client_devices`,
      {
        params: {
          page,
          pageSize,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error.response;
  }
}

async function fetchEvents({ state, commit }, { customerId, options }) {
  try {
    let search = encodeURI(JSON.stringify(options));
    const response = await axios.get(
      `v2/customers/${customerId}/events?search=${search}`
    );
    return response.data;
  } catch (error) {
    throw error.response;
  }
}

async function fetchDeviceOwnership({ commit }, { customerId }) {
  try {
    const response = await axios.get(
      `v2/customers/${customerId}/device_history`
    );
    return response.data;
  } catch (error) {
    throw error.response;
  }
}

async function fetchUserLogs({ commit }, { customerId, options }) {
  try {
    let search = encodeURIComponent(JSON.stringify(options));
    const response = await axios.get(
      `v2/customers/${customerId}/customer_logs?search=${search}`
    );
    return response.data;
  } catch (error) {
    throw error.response;
  }
}

async function fetchNotes({ getters, commit }, { commentableId }) {
  const global = { root: true };
  try {
    const response = await axios.get(
      // commentable_type should be a parameter: Profile => Device, CustomerProfile => Customer
      `/v2/comments.json?commentable_id=${commentableId}&commentable_type=Profile`
    );
    const comments = get(response.data, "comments");
    commit(`db/device/${types.FETCH_NOTES.SUCCESS}`, comments, global);
    return response.data;
  } catch (error) {
    commit(`db/device/${types.FETCH_NOTES.ERROR}`, error.response, global);
    throw error.response;
  }
}

async function addNote({ getters, commit }, { commentableId, note }) {
  const response = await axios.post(
    `/v2/comments.json?commentable_id=${commentableId}&commentable_type=Profile`,
    { comments: { content: note } }
  );
  return response.data;
}

async function deleteNote({ getters, commit }, { commentableId }) {
  const response = await axios.delete(`/v2/comments/${commentableId}.json`);
  return response.data;
}

async function fetchTags({ getters, commit }, { taggableId }) {
  const global = { root: true };
  try {
    const response = await axios.get(
      `/v2/tags.json?taggable_id=${taggableId}&taggable_type=Profile`
    );
    const tags = get(response, "data.tag_list", []);
    commit(`db/device/${types.FETCH_TAGS.SUCCESS}`, tags, global);
    return tags;
  } catch (error) {
    commit(`db/device/${types.FETCH_TAGS.ERROR}`, error.response, global);
    throw error;
  }
}

async function updateTags({ getters, commit }, { taggableId, list }) {
  const response = await axios.put(
    `/v2/tags/update_tags.json?taggable_id=${taggableId}&taggable_type=Profile`,
    { tag_list: list }
  );
  if (response.status === 204) {
    commit(
      `db/device/${types.FETCH_TAGS.SUCCESS}`,
      list.map((tag) => ({ text: tag })),
      { root: true }
    );
  }
  return response.data;
}

async function verifyRingCustomer({}, { customerId }) {
  try {
    const response = await axios.post(
      `/v2/customers/${customerId}/send_validation_email`
    );
    return response.data;
  } catch (e) {
    throw e;
  }
}

async function authorizeLogin({}, { customerId }) {
  try {
    const response = await axios.post(`/v2/customers/${customerId}/login_auth`);
    return response.data;
  } catch (e) {
    throw e;
  }
}

async function generateCCOTP({}, { customerId }) {
  try {
    const response = await axios.post(
      `/v3/customers/${customerId}/account_recovery/generate_ccotp`
    );
    return response.data;
  } catch (e) {
    throw e;
  }
}

async function fetchAuthorizationChannels({}, { customerId }) {
  try {
    const response = await axios.get(
      `/v2/customers/dsb_channels?customer_id=${customerId}`
    );
    return response.data;
  } catch (e) {
    throw e;
  }
}

async function setScreensharing({ commit }, { enabled }) {
  commit(types.SCREEN_SHARING, {
    enabled,
  });
}

export default {
  globalSearch,
  fetchAppActivityEvents,
  fetchAppActivityProfile,
  fetchNotes,
  addNote,
  deleteNote,
  fetchTags,
  updateTags,
  updateCustomer,
  updateCustomerEmail,
  resetPassword,
  fetchEvents,
  verifyRingCustomer,
  fetchAuthorizationChannels,
  fetchDeviceOwnership,
  fetchUserLogs,
  setScreensharing,
  authorizeLogin,
  generateCCOTP,
  getActiveAuthorizationChannels,
  setIsFirstAuthorizationAttempt,
  getSearchCustomerEndpoint,
};
